import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

import Link from '../Link';

type SimpleProps = {
  simpleDiscountAmount?: string;
  simpleTitle?: string;
};

type FullProps = {
  complimentaryColor?: string;
  label?: string;
  discountTitle?: string;
  discountSubTitle?: string;
  lines?: string[];
};

type AmountProps = {
  amount?: string;
  cardTitle?: string;
  accentColor?: string;
};

type LinkCardProps = {
  cardTitle?: string;
  accentColor?: string;
  validUntil?: string;
  href?: string;
};

type Props = SimpleProps &
  FullProps &
  AmountProps &
  LinkCardProps & {
    backgroundColor?: string;
    variant?: 'simple' | 'full' | 'amount' | 'link';
    className?: string;
  };

const Layout = cva('rounded-2', {
  variants: {
    variant: {
      simple: 'py-12.5 pl-5 px-3 text-white',
      full: 'relative uppercase text-white',
      amount: 'shadow-lg bg-white p-5',
      link: ' bg-white p-5 transition-shadow shadow-md focus-visible:outline focus-visible:outline-2',
    },
  },
});

const Simple = ({ simpleDiscountAmount, simpleTitle }: SimpleProps) => {
  return (
    <>
      <h2 className="leading-60 text-4xl">{simpleDiscountAmount}</h2>
      <p className="leading-48 text-2xl">{simpleTitle}</p>
    </>
  );
};

const Full = ({ complimentaryColor, label, discountTitle, discountSubTitle, lines }: FullProps) => {
  return (
    <>
      {label && (
        <div className="bg-yellow rounded-l-50 text-gray-dark absolute right-0 top-0 mt-2 py-1.5 pl-5 pr-3 text-sm capitalize">
          {label}
        </div>
      )}
      <div className="px-3 pb-5 pt-8">
        <h3 className="text-2xl">{discountTitle}</h3>
        <h4 className="leading-14 mt-3 text-xs">{discountSubTitle}</h4>
      </div>

      <div className="rounded-b-2 relative px-3 pb-5 pt-3" style={{ backgroundColor: `${complimentaryColor ?? ''}` }}>
        {lines?.length && (
          <div>
            {lines?.map((line, i) => (
              <p className="mb-3 text-sm" key={i}>
                {line}
              </p>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

const Amount = ({ amount, cardTitle, accentColor }: AmountProps) => {
  return (
    <>
      <p className="text-2xl font-bold" style={{ color: accentColor }}>
        {amount}
      </p>
      <p className="mt-1 text-base">{cardTitle}</p>
    </>
  );
};

const LinkContent = ({ cardTitle, accentColor, validUntil }: Omit<LinkCardProps, 'href'>) => {
  return (
    <div className="flex h-full flex-col">
      <p className="text-base font-bold" style={{ color: accentColor }}>
        {cardTitle}
      </p>
      {validUntil && <p className="mt-1 text-sm">{validUntil}</p>}
      <div className="mt-auto flex justify-end pt-3">
        <FontAwesomeIcon aria-hidden icon={faArrowRight} />
      </div>
    </div>
  );
};

const DiscountTile = ({
  variant = 'full',
  backgroundColor,
  simpleDiscountAmount,
  simpleTitle,
  amount,
  cardTitle,
  accentColor,
  validUntil,
  href,
  className,
  ...props
}: Props) => {
  const classes = twMerge(Layout({ variant }), className);
  const inlineStyle = backgroundColor ? { backgroundColor } : undefined;

  if (variant === 'amount') {
    return (
      <div className={classes} style={inlineStyle}>
        <Amount accentColor={accentColor} amount={amount} cardTitle={cardTitle} />
        {validUntil && <p className="mt-1 text-sm">{validUntil}</p>}
      </div>
    );
  }

  if (variant === 'link') {
    const content = <LinkContent accentColor={accentColor} cardTitle={cardTitle} validUntil={validUntil} />;

    if (href) {
      return (
        <div className={classes}>
          <Link anchorClassName="block h-full" className="block h-full" href={href}>
            {content}
          </Link>
        </div>
      );
    }

    return (
      <div className={classes} style={inlineStyle}>
        {content}
      </div>
    );
  }

  return (
    <div className={classes} style={{ backgroundColor: `${backgroundColor ?? ''}` }}>
      {variant === 'simple' && <Simple simpleDiscountAmount={simpleDiscountAmount} simpleTitle={simpleTitle} />}
      {variant === 'full' && <Full {...props} />}
    </div>
  );
};

export default DiscountTile;
